/**
 * Unauthenticated landing page for the per-meeting "Add to agenda" link in the pre-meeting
 * nudge email. Reads ?token=, fetches meeting context from the add-agenda-from-email edge
 * function (GET), renders a tiny form, then POSTs the submitted title.
 *
 * No sign-in required - the HMAC-signed token (recipient + workspace + meeting + action) is the
 * auth surface. Verified server-side.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { edgeFnUrl } from '@saas-boilerplate/shared';

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

type MeetingContext = {
  meeting_id: string;
  meeting_title: string;
  starts_at: string | null;
  ends_at: string | null;
  status: string;
};

async function callAddAgenda(method: 'GET' | 'POST', token: string, body?: { title: string }): Promise<any> {
  const base = edgeFnUrl('add-agenda-from-email');
  const url = method === 'GET' ? `${base}?token=${encodeURIComponent(token)}` : base;
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(ANON_KEY ? { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` } : {}),
    },
    body: method === 'POST' ? JSON.stringify({ token, ...body }) : undefined,
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function AddAgendaPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meeting, setMeeting] = useState<MeetingContext | null>(null);
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ savedTitle: string } | null>(null);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('This link is missing its token.');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ctx = (await callAddAgenda('GET', token)) as MeetingContext;
        if (!cancelled) setMeeting(ctx);
      } catch (e: any) {
        if (!cancelled) setError(humanize(e?.message || 'Unable to load meeting'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async () => {
    if (!title.trim() || !token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = (await callAddAgenda('POST', token, { title: title.trim() })) as {
        ok: boolean;
        agenda_item: { id: string; title: string };
      };
      setSubmitted({ savedTitle: res.agenda_item.title });
    } catch (e: any) {
      setError(humanize(e?.message || 'Could not add agenda item'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4 py-12">
      <Card className="w-full max-w-md">
        {loading ? (
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        ) : error && !meeting ? (
          <>
            <CardHeader>
              <div className="mb-2 flex items-center gap-2 text-rose-600">
                <AlertCircle className="h-5 w-5" />
                <CardTitle>Link expired</CardTitle>
              </div>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <a href="/">Open Upstand</a>
              </Button>
            </CardContent>
          </>
        ) : submitted ? (
          <>
            <CardHeader>
              <div className="mb-2 flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-5 w-5" />
                <CardTitle>Added to the agenda</CardTitle>
              </div>
              <CardDescription>
                "{submitted.savedTitle}" - added to <strong>{meeting?.meeting_title}</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setSubmitted(null);
                  setTitle('');
                }}
              >
                Add another
              </Button>
              <Button asChild className="w-full">
                <a href="/">Open Upstand</a>
              </Button>
            </CardContent>
          </>
        ) : meeting ? (
          <>
            <CardHeader>
              <CardTitle>Add to the agenda</CardTitle>
              <CardDescription>
                <strong>{meeting.meeting_title}</strong>
                {meeting.starts_at && <> · {formatWhen(meeting.starts_at)}</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="agenda-title">Agenda item</Label>
                <Input
                  id="agenda-title"
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="What should be discussed?"
                  maxLength={280}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && title.trim() && !submitting) {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                />
              </div>
              {error && (
                <p className="flex items-center gap-2 text-sm text-rose-600">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </p>
              )}
              <Button className="w-full" disabled={!title.trim() || submitting} onClick={() => void submit()}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add to agenda'}
              </Button>
            </CardContent>
          </>
        ) : null}
      </Card>
    </div>
  );
}

function humanize(msg: string): string {
  if (msg === 'invalid_token') return 'This link has expired or been tampered with.';
  if (msg === 'meeting_not_found') return 'That meeting is no longer in Upstand.';
  if (msg === 'missing_title') return 'Add a description for the agenda item.';
  if (msg === 'title_too_long') return 'That agenda item is too long - keep it under 280 characters.';
  return msg;
}
